import { cn } from "./cn";

/**
 * Port of the reference's StatusBadge (src/components/ui/StatusBadge.tsx).
 * The color/dot table is ported verbatim — that's UI styling policy. The
 * reference derives its label text from a domain helper
 * (src/lib/session-status.ts, #899) that encodes backend session-status
 * business rules (status aliasing, signature-aware vs. flat labeling). That
 * module is domain/data logic, not design system — outside WS-B's owned
 * paths and not yet ported by any lane. StatusBadge instead takes an
 * optional `label` override for callers that own that mapping, and falls
 * back to a generic humanized form of `status` otherwise.
 */
const STATUS_CONFIG: Record<string, { bg: string; text: string; dot: string }> = {
  generated: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  sow_generated: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  awaiting_signature: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  sow_executed: { bg: "bg-accent/10", text: "text-accent", dot: "bg-accent" },
  complete: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  in_progress: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  draft: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  cancelled: { bg: "bg-elevated", text: "text-ink-light", dot: "bg-ink-faint" },
  archived: { bg: "bg-elevated", text: "text-ink-light", dot: "bg-ink-faint" },
  declined: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  voided: { bg: "bg-elevated", text: "text-ink-light", dot: "bg-ink-faint" },
  converted: { bg: "bg-accent/10", text: "text-accent", dot: "bg-accent" },
  expired: { bg: "bg-elevated", text: "text-ink-light", dot: "bg-ink-faint" },
  inactive: { bg: "bg-elevated", text: "text-ink-light", dot: "bg-ink-faint" },
  active: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  coming_soon: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
};

const FALLBACK = { bg: "bg-elevated", text: "text-ink-light", dot: "bg-ink-faint" };

// Same "snake_case -> Title case" fallback RoleBadge uses. Callers that own
// the real session-status label policy pass `label` and skip this.
function humanize(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface StatusBadgeProps {
  status: string;
  label?: string;
  className?: string;
  showDot?: boolean;
}

export function StatusBadge({ status, label, className, showDot = true }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] ?? FALLBACK;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        config.bg,
        config.text,
        className,
      )}
    >
      {showDot && (
        <span className={cn("mr-1.5 h-1.5 w-1.5 rounded-full", config.dot)} aria-hidden="true" />
      )}
      {label ?? humanize(status)}
    </span>
  );
}
